import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useLanguage } from '../LanguageContext';
import HelloKittyBride from '../assets/images/hellokitty_bride.webp';
import HelloKittyGroom from '../assets/images/hellokitty_groom.webp';
import LanguageToggle from '../components/LanguageToggle';
import pages from '../pages';

export default function MobileStory({ guestDetails }) {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const initialIndex = location.state?.pageIndex ?? 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(initialIndex);
  const [showTabs, setShowTabs] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Scroll to top when changing pages
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setShowTabs(true);
  }, [currentPageIndex]);

  useEffect(() => {
    navigate('.', { replace: true, state: null });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Hide tabs when scrolling down past 50px, show tabs when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setShowTabs(false);
      } else if (currentScrollY < lastScrollY) {
        setShowTabs(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const page = pages[currentPageIndex];

  return (
    <div className={`relative min-h-screen flex flex-col`}>
      {/* HEADER AT THE TOP */}
      <header
        className={`nav-topbar fixed top-0 left-0 right-0 z-50 shadow-sm transition-all duration-300 overflow-hidden ${showTabs ? 'h-[96px]' : 'h-[54px]'}`}
      >
        <div className="flex flex-col items-center pt-3 pb-1 px-4">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-row">
              <img
                src={HelloKittyGroom}
                alt="Groom"
                className="max-w-9 max-h-9"
              />
              <img
                src={HelloKittyBride}
                alt="Bride"
                className="max-w-9 max-h-9"
              />
            </div>
            <h1 className="text-3xl text-top-title select-none flex-1 text-center">
              Daniel & Tracer
            </h1>
            <LanguageToggle className="btn-language-toggle shrink-0 ml-2 mr-1" />
          </div>
          <div
            className={`flex items-center w-full mt-2 space-x-4 transition-all duration-300 ${showTabs ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
          >
            <nav className="flex space-x-6 overflow-x-auto no-scrollbar flex-1 justify-start px-4">
              {pages.map((pageItem, index) => (
                <button
                  key={pageItem.id}
                  onClick={() => setCurrentPageIndex(index)}
                  className={`
                    text-nav-text nav-tab-base
                    ${currentPageIndex === index ? 'nav-tab-active' : 'nav-tab-nonactive'}
                  `}
                >
                  {t('nav.' + pageItem.key) || pageItem.title}
                  {currentPageIndex === index && (
                    <motion.div
                      layoutId="activeTabUnderline"
                      className="nav-tab-underline"
                      transition={{
                        type: 'spring',
                        stiffness: 380,
                        damping: 30,
                      }}
                    />
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* CONTENT AREA */}
      <div className="pt-[96px] flex-grow flex flex-col">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPageIndex}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="flex-grow flex flex-col w-full"
          >
            {/* Cover Image */}
            {!page.hideImage && (
              <div className="relative w-full overflow-hidden transition-all duration-500 ease-in-out h-[50vh]">
                <img
                  src={page.image}
                  alt={page.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[radial-gradient(farthest-corner,var(--tw-gradient-stops))] from-black/20 to-transparent" />

                <div className="absolute inset-0 flex flex-col items-center justify-center text-white pointer-events-none p-4">
                  <h2 className="text-7xl text-banner-title text-center">
                    {t(page.titleKey)}
                  </h2>
                  {page.subtitleKey && (
                    <p className="mt-1 text-3xl text-banner-subtitle text-center">
                      {t(page.subtitleKey)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Page Component / Text */}
            <div
              className={`flex-grow w-full relative z-10 shadow-md bg-wedding-blush ${page?.hideImage ? '' : 'px-8 pt-8 pb-20'}`}
            >
              {page.mobileComponent || page.component}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
